/**
 * Feature-based Help Categories
 * 
 * Maps help articles to actual dashboard features for better organization
 * and consistency with HomeDashboard feature discovery
 */

import { HelpArticleData } from './types';
import { 
  FileText, 
  Shield, 
  Building2, 
  Users, 
  Search, 
  MessageSquare, 
  Package, 
  Layers,
  Briefcase,
  Database,
  ListChecks
} from 'lucide-react';

export interface FeatureCategory {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  section: 'rfp-agent' | 'core';
  order: number;
  dashboardPath?: string;
  subFeatures?: string[];
}

/**
 * RFP Agent Feature Categories (matching HomeDashboard sidebar items)
 */
export const RFP_AGENT_FEATURES: FeatureCategory[] = [
  {
    id: 'proposal-management',
    name: 'Proposal Management',
    description: 'List, Search, Requirements, Matching, Pipeline, Review',
    icon: FileText,
    section: 'rfp-agent',
    order: 1,
    dashboardPath: '/rfp/proposals-management',
    subFeatures: ['List', 'Search', 'Requirements', 'Matching', 'Pipeline', 'Review']
  },
  {
    id: 'government-data',
    name: 'Government Data',
    description: 'Integrations, Opportunities, Awards, News',
    icon: Shield,
    section: 'rfp-agent',
    order: 2,
    dashboardPath: '/rfp/external-data',
    subFeatures: ['Integrations', 'Opportunities', 'Awards', 'News']
  },
  {
    id: 'data-pipeline',
    name: 'Data Pipeline',
    description: 'Sources, Artifacts, and Data Configuration',
    icon: Building2,
    section: 'rfp-agent',
    order: 3,
    dashboardPath: '/data',
    subFeatures: ['Sources', 'Artifacts', 'Data Configuration']
  },
  {
    id: 'staff-management',
    name: 'Staff Management',
    description: 'Team qualifications and matching',
    icon: Users,
    section: 'rfp-agent',
    order: 4,
    dashboardPath: '/rfp/staff-data',
    subFeatures: ['Team Qualifications', 'Staff Matching']
  },
];

/**
 * Core Platform Feature Categories (matching HomeDashboard baseline features)
 */
export const CORE_FEATURES: FeatureCategory[] = [
  {
    id: 'universal-ai-search',
    name: 'Universal AI Search',
    description: 'AI-powered semantic search across all data',
    icon: Search,
    section: 'core',
    order: 1,
    dashboardPath: '/search',
  },
  {
    id: 'ai-chat',
    name: 'AI Chat',
    description: 'Conversational AI interface for Q&A',
    icon: MessageSquare,
    section: 'core',
    order: 2,
    dashboardPath: '/chat',
  },
  {
    id: 'knowledge-base',
    name: 'Knowledge Base',
    description: 'Document management and search',
    icon: Package,
    section: 'core',
    order: 3,
    dashboardPath: '/knowledge',
  },
  {
    id: 'entity-explorer',
    name: 'Entity Explorer',
    description: 'Browse and query extracted entities',
    icon: Layers,
    section: 'core',
    order: 4,
    dashboardPath: '/entities',
  },
];

/**
 * Map article IDs to feature categories
 */
export const ARTICLE_TO_FEATURE_MAP: Record<string, string> = {
  // Proposal Management articles
  'rfp-proposal-management-dashboard': 'proposal-management',
  'rfp-proposal-list-tab': 'proposal-management',
  'rfp-proposal-search-tab': 'proposal-management',
  'rfp-requirements-tab': 'proposal-management',
  'rfp-opportunity-matching-tab': 'proposal-management',
  'rfp-pipeline-tab': 'proposal-management',
  'rfp-review-workflow-tab': 'proposal-management',
  'rfp-proposal-generation-workflow': 'proposal-management',
  'rfp-proposal-builder-component': 'proposal-management',
  'rfp-ai-template-generation': 'proposal-management',
  'rfp-ai-proposal-generation': 'proposal-management',
  'rfp-ai-section-editing': 'proposal-management',
  'rfp-ai-keyword-suggestions': 'proposal-management',
  'rfp-ai-opportunity-summarization': 'proposal-management',
  'rfp-ai-feasibility-assessment': 'proposal-management',
  'rfp-citation-reference-system': 'proposal-management',
  'rfp-evidence-basket-system': 'proposal-management',
  'rfp-review-checklist-system': 'proposal-management',
  'rfp-end-to-end-proposal-creation': 'proposal-management',
  'rfp-end-to-end-full-demo': 'proposal-management',
  
  // Government Data articles
  'rfp-government-data-dashboard': 'government-data',
  'rfp-opportunities-tab': 'government-data',
  'rfp-integrations-tab': 'government-data',
  'rfp-news-tab': 'government-data',
  'rfp-awards-tab': 'government-data',
  'rfp-zone-management': 'government-data',
  'rfp-data-source-integrations-detailed': 'government-data',
  'rfp-data-sources': 'government-data',
  'rfp-sync-management': 'government-data',
  'rfp-rss-feed-management': 'government-data',
  'rfp-us-government-integrations': 'government-data',
  'rfp-international-data-sources': 'government-data',
  'rfp-end-to-end-integrations-data-mgmt': 'government-data',
  'rfp-end-to-end-search-bar': 'government-data',
  
  // Data Pipeline articles
  'rfp-knowledge-base-overview': 'data-pipeline',
  'rfp-environment-configuration': 'data-pipeline',
  
  // Staff Management articles
  'rfp-staff-matching-dashboard': 'staff-management',
  'rfp-staff-matching-results': 'staff-management',
  'rfp-matching-rules-management': 'staff-management',
  'rfp-rule-conditions': 'staff-management',
  'rfp-score-calculation': 'staff-management',
  'rfp-staff-data-integration': 'staff-management',
  'rfp-qualification-management': 'staff-management',
  'rfp-staff-matching-best-practices': 'staff-management',
  'rfp-staff-matching': 'staff-management',
  
  // Getting Started (maps to Proposal Management as primary feature)
  'rfp-getting-started': 'proposal-management',
  
  // Service Lines, Geographic, etc. (maps to Government Data)
  'rfp-service-lines': 'government-data',
  'rfp-geographic-filtering': 'government-data',
  'rfp-deduplication': 'government-data',
  'rfp-credentials': 'government-data',
  
  // Core mappings (for non-plugin articles)
  'rag-chat': 'universal-ai-search',
  'knowledge-base': 'knowledge-base',
  'entities': 'entity-explorer',
  'sources': 'data-pipeline',
};

/**
 * Get feature category for an article based on its ID, category, or plugin
 */
export function getFeatureCategoryForArticle(article: HelpArticleData): FeatureCategory | null {
  // First, try direct article ID mapping
  if (ARTICLE_TO_FEATURE_MAP[article.id]) {
    const featureId = ARTICLE_TO_FEATURE_MAP[article.id];
    return [...RFP_AGENT_FEATURES, ...CORE_FEATURES].find(f => f.id === featureId) || null;
  }
  
  // If it's an RFP Agent article, try to map by category and keywords
  if (article.plugin === 'rfp-agent') {
    // Map old categories to new features based on article content
    const categoryMap: Record<string, string> = {
      'plugins': 'proposal-management', // Default for plugin articles
      'sources': 'government-data',
      'getting-started': 'proposal-management',
      'admin': 'staff-management',
      'workflows': 'proposal-management',
    };
    
    // Also check keywords for more specific mapping
    const keywords = article.keywords || [];
    if (keywords.some(k => k.includes('staff') || k.includes('matching') || k.includes('qualification'))) {
      return RFP_AGENT_FEATURES.find(f => f.id === 'staff-management') || null;
    }
    if (keywords.some(k => k.includes('government') || k.includes('opportunity') || k.includes('integration') || k.includes('data-source'))) {
      return RFP_AGENT_FEATURES.find(f => f.id === 'government-data') || null;
    }
    if (keywords.some(k => k.includes('proposal') || k.includes('template') || k.includes('generation') || k.includes('review'))) {
      return RFP_AGENT_FEATURES.find(f => f.id === 'proposal-management') || null;
    }
    
    const featureId = categoryMap[article.category] || 'proposal-management';
    return RFP_AGENT_FEATURES.find(f => f.id === featureId) || RFP_AGENT_FEATURES[0];
  }
  
  // For core articles, map by category
  const coreCategoryMap: Record<string, string> = {
    'rag-chat': 'universal-ai-search',
    'knowledge-base': 'knowledge-base',
    'entities': 'entity-explorer',
    'sources': 'data-pipeline',
  };
  
  const featureId = coreCategoryMap[article.category];
  if (featureId) {
    return CORE_FEATURES.find(f => f.id === featureId) || null;
  }
  
  return null;
}

/**
 * Group articles by feature category and subcategory
 */
export function groupArticlesByFeature(articles: HelpArticleData[]): Map<string, HelpArticleData[]> {
  const grouped = new Map<string, HelpArticleData[]>();
  
  // Initialize all feature categories
  [...RFP_AGENT_FEATURES, ...CORE_FEATURES].forEach(feature => {
    grouped.set(feature.id, []);
  });
  
  // Group articles
  articles.forEach(article => {
    const feature = getFeatureCategoryForArticle(article);
    if (feature) {
      const existing = grouped.get(feature.id) || [];
      grouped.set(feature.id, [...existing, article]);
    }
  });
  
  // Sort articles within each feature by priority, then by subcategory, then by title
  grouped.forEach((articles, featureId) => {
    articles.sort((a, b) => {
      // First by priority
      const priorityA = a.priority || 0;
      const priorityB = b.priority || 0;
      if (priorityA !== priorityB) {
        return priorityB - priorityA; // Higher priority first
      }
      
      // Then by subcategory (Getting Started and End-to-End first)
      const subcatOrder: Record<string, number> = {
        'Getting Started': 1,
        'End-to-End Workflows': 2,
        'Core Features': 3,
        'Advanced': 4,
      };
      const subcatA = subcatOrder[a.subcategory || ''] || 99;
      const subcatB = subcatOrder[b.subcategory || ''] || 99;
      if (subcatA !== subcatB) {
        return subcatA - subcatB;
      }
      
      // Finally by title
      return a.title.localeCompare(b.title);
    });
  });
  
  return grouped;
}

/**
 * Group articles by subcategory within a feature
 */
export function groupArticlesBySubcategory(articles: HelpArticleData[]): Map<string, HelpArticleData[]> {
  const grouped = new Map<string, HelpArticleData[]>();
  
  articles.forEach(article => {
    const subcategory = article.subcategory || 'Other';
    const existing = grouped.get(subcategory) || [];
    grouped.set(subcategory, [...existing, article]);
  });
  
  // Sort subcategories by priority
  const subcatOrder: Record<string, number> = {
    'Getting Started': 1,
    'End-to-End Workflows': 2,
    'Core Features': 3,
    'Advanced': 4,
    'Other': 99,
  };
  
  // Sort articles within each subcategory
  grouped.forEach((articles, subcategory) => {
    articles.sort((a, b) => {
      const priorityA = a.priority || 0;
      const priorityB = b.priority || 0;
      if (priorityA !== priorityB) {
        return priorityB - priorityA;
      }
      return a.title.localeCompare(b.title);
    });
  });
  
  return grouped;
}

/**
 * Calculate reading time in minutes based on content length
 * Average reading speed: 200-250 words per minute
 */
export function calculateReadingTime(content: string): number {
  // Remove markdown syntax and count words
  const text = content
    .replace(/```[\s\S]*?```/g, '') // Remove code blocks
    .replace(/`[^`]*`/g, '') // Remove inline code
    .replace(/\[([^\]]*)\]\([^\)]*\)/g, '$1') // Convert links to text
    .replace(/[#*\-_=~`]/g, '') // Remove markdown formatting
    .replace(/\n+/g, ' ') // Replace newlines with spaces
    .trim();
  
  const wordCount = text.split(/\s+/).filter(word => word.length > 0).length;
  const readingSpeed = 225; // words per minute (average)
  const minutes = Math.ceil(wordCount / readingSpeed);
  
  return Math.max(1, minutes); // At least 1 minute
}

/**
 * Get all feature categories (RFP Agent + Core)
 */
export function getAllFeatureCategories(): FeatureCategory[] {
  return [...RFP_AGENT_FEATURES, ...CORE_FEATURES].sort((a, b) => {
    // RFP Agent first, then Core
    if (a.section !== b.section) {
      return a.section === 'rfp-agent' ? -1 : 1;
    }
    return a.order - b.order;
  });
}

